/**
 * Canonical MTSS Support Areas Taxonomy
 * 
 * This file centralizes the taxonomy for support areas used throughout the app.
 * All components should import from here to ensure consistency.
 * 
 * SUPPORT_AREAS = Broad MTSS domains (used for student concern_area, intervention category)
 * SKILL_FOCUS_OPTIONS = Specific targeted skills (used for intervention subcategories, target_skill)
 */

// Canonical broad support areas - used across Add/Edit Student and Add/Edit Intervention
export const SUPPORT_AREAS = [
  'Reading',
  'Writing',
  'Math',
  'Behavior / Executive Function',
  'Speech / Language',
  'Social Skills',
  'Attendance / Engagement',
  'Other'
];

// Legacy value mappings for backward compatibility (display normalization)
// Maps old/inconsistent values to canonical values
export const LEGACY_SUPPORT_AREA_MAP = {
  'Behavior': 'Behavior / Executive Function',
  'Speech/Language': 'Speech / Language',
  'Attendance': 'Attendance / Engagement',
  'Other / Custom': 'Other',
  'General': 'Other'
};

// Reverse mapping: canonical to all accepted legacy values (for filtering/matching)
export const CANONICAL_TO_LEGACY_MAP = {
  'Behavior / Executive Function': ['Behavior', 'Behavior / Executive Function'],
  'Speech / Language': ['Speech/Language', 'Speech / Language'],
  'Attendance / Engagement': ['Attendance', 'Attendance / Engagement'],
  'Other': ['Other', 'Other / Custom', 'General']
};

/**
 * Normalize a support area value to canonical form
 * @param {string} value - The value to normalize
 * @returns {string} - The canonical value
 */
export const normalizeSupportArea = (value) => {
  if (!value) return value;
  return LEGACY_SUPPORT_AREA_MAP[value] || value;
};

/**
 * Normalize an array of support area values to canonical form
 * @param {string[]} values - Array of values to normalize
 * @returns {string[]} - Array of canonical values
 */
export const normalizeSupportAreaArray = (values) => {
  if (!Array.isArray(values)) return values;
  return values.map(normalizeSupportArea);
};

/**
 * Get all values that should match a canonical support area (for filtering)
 * @param {string} canonical - The canonical value
 * @returns {string[]} - Array of values that match
 */
export const getMatchingValues = (canonical) => {
  return CANONICAL_TO_LEGACY_MAP[canonical] || [canonical];
};

// Specific skill focus options organized by support area
// These are used as intervention subcategories
export const SKILL_FOCUS_BY_AREA = {
  'Reading': [
    'Reading Fluency',
    'Decoding',
    'Phonics',
    'Phonological Awareness',
    'Reading Comprehension',
    'Vocabulary',
    'Sight Words',
    'Oral Reading Fluency',
    'Spelling',
    'Written Response to Reading'
  ],
  'Writing': [
    'Sentence Writing',
    'Paragraph Writing',
    'Written Expression',
    'Grammar',
    'Editing/Revising',
    'Spelling',
    'Handwriting',
    'Writing Organization'
  ],
  'Math': [
    'Math Facts Fluency',
    'Calculation',
    'Computation',
    'Word Problems',
    'Number Sense',
    'Fractions',
    'Algebra Readiness',
    'Multi-Step Problem Solving'
  ],
  'Behavior / Executive Function': [
    'Self-Regulation',
    'Check-In/Check-Out',
    'Attention to Task',
    'Task Initiation',
    'Work Completion',
    'Impulse Control',
    'Organization',
    'Emotional Regulation',
    // Moved from former "General" category
    'Study Skills',
    'Homework Completion',
    'Test-Taking Strategies'
  ],
  'Social Skills': [
    'Social Skills',
    'Conversation Skills',
    'Turn Taking',
    'Peer Interaction',
    'Perspective Taking',
    'Flexible Thinking',
    'Conflict Resolution',
    'Group Participation'
  ],
  'Speech / Language': [
    'Articulation',
    'Speech Sound Production',
    'Receptive Language',
    'Expressive Language',
    'Pragmatic Language',
    'Vocabulary Development',
    'Narrative Language',
    'Syntax / Grammar',
    'Following Directions',
    'Fluency',
    'Voice'
  ],
  'Attendance / Engagement': [
    'Attendance',
    'School Engagement',
    'Class Participation',
    'Transition Support'
  ],
  'Other': []  // Custom interventions - no predefined subcategories
};

// Legacy category mapping for intervention categories
export const LEGACY_INTERVENTION_CATEGORY_MAP = {
  'Behavior': 'Behavior / Executive Function',
  'Speech/Language': 'Speech / Language',
  'Speech / Language': 'Speech / Language',
  'Attendance': 'Attendance / Engagement',
  'Attendance / Engagement': 'Attendance / Engagement',
  'Other / Custom': 'Other',
  'General': 'Other'  // General maps to Other
};

/**
 * Normalize an intervention category to canonical form
 * @param {string} category - The category to normalize
 * @returns {string} - The canonical category
 */
export const normalizeInterventionCategory = (category) => {
  if (!category) return category;
  return LEGACY_INTERVENTION_CATEGORY_MAP[category] || category;
};

/**
 * Infer category from intervention name (for legacy data)
 * @param {string} name - The intervention name
 * @returns {{ category: string, subcategory: string }}
 */
export const inferCategoryFromName = (name) => {
  if (!name) return { category: '', subcategory: '' };
  
  for (const [category, subcategories] of Object.entries(SKILL_FOCUS_BY_AREA)) {
    if (subcategories.includes(name)) {
      return { category, subcategory: name };
    }
    if (category === name) {
      return { category, subcategory: '' };
    }
  }
  
  // Check legacy category names
  const normalizedCategory = normalizeInterventionCategory(name);
  if (SUPPORT_AREAS.includes(normalizedCategory)) {
    return { category: normalizedCategory, subcategory: '' };
  }
  
  // Not found in standard categories - treat as custom
  return { category: 'Other', subcategory: '' };
};
